/**
 * Login — Awwwards/Dribbble-grade split-screen auth page
 *
 * Left panel (60%): Cinematic background with deep gradient, large Kantera wordmark,
 *                   floating animated orbs, tagline text
 * Right panel (40%): Dark glass card with email/password form, Google OAuth, sign-up link
 */

import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useResponsive } from '../hooks/useResponsive';
import { C, injectKeyframes, inputStyle as baseInputStyle, orbStyle } from '@kantera/ui';
import { setBorderColor } from '../utils/borderColor';

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

const EyeIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.05 12.04c-.03-2.7 2.2-3.99 2.3-4.06-1.25-1.84-3.2-2.09-3.9-2.12-1.66-.17-3.24.97-4.08.97-.84 0-2.14-.95-3.52-.92-1.81.03-3.48 1.05-4.41 2.67-1.88 3.26-.48 8.08 1.35 10.73.9 1.3 1.97 2.75 3.38 2.7 1.36-.06 1.87-.88 3.51-.88 1.64 0 2.1.88 3.53.85 1.46-.03 2.38-1.32 3.27-2.62 1.03-1.5 1.46-2.96 1.48-3.03-.03-.02-2.84-1.09-2.87-4.33zM14.5 4.2c.74-.9 1.24-2.15 1.1-3.4-1.07.05-2.36.71-3.13 1.6-.69.8-1.29 2.07-1.13 3.29 1.19.09 2.41-.6 3.16-1.49z" />
  </svg>
);

// Kantera brand mark — official low-poly ball glyph
const KanteraGlyph = () => (
  <img
    src="/assets/brand/kantera-mark.png"
    alt="Kantera"
    width={64}
    height={64}
    style={{ objectFit: 'contain', display: 'block' }}
  />
);

// ---------------------------------------------------------------------------
// Inline focus/hover helpers (replaces a previously-injected stylesheet element)
// ---------------------------------------------------------------------------
const applyInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
  setBorderColor(e.currentTarget, C.borderHover);
  e.currentTarget.style.boxShadow = `0 0 0 3px ${C.accent.indigoSoft}`;
};
const removeInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
  setBorderColor(e.currentTarget, C.border);
  e.currentTarget.style.boxShadow = 'none';
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const Login: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    signIn,
    supabase,
    user,
    tenant,
    loading: authLoading,
    passwordRecovery,
    authError,
    clearAuthError,
  } = useAuth();
  const { isMobile, isTablet } = useResponsive();
  const isNarrow = isMobile || isTablet;

  // Login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Password reset state
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Sign In — Kantera';
    injectKeyframes();
  }, []);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Judge the form here rather than letting the provider judge it. Neither
    // field carried `required`, so an empty submit used to post to Supabase,
    // take a 400, and surface the provider's own string — "missing email or
    // phone", which names a field Kantera does not have and never mentions the
    // password. Say what is actually missing, and spend no round-trip on a
    // question the page can already answer. C27.
    const missing: string[] = [];
    if (!email.trim()) missing.push('email address');
    if (!password) missing.push('password');
    if (missing.length > 0) {
      setError(`Please enter your ${missing.join(' and ')}.`);
      return;
    }

    setLoading(true);
    try {
      await signIn(email, password);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError(null);
    if (!resetEmail.trim()) {
      setResetError('Please enter your email address.');
      return;
    }
    setResetLoading(true);
    try {
      const { error: supaError } = await supabase.auth.resetPasswordForEmail(resetEmail.trim(), {
        redirectTo: window.location.origin + '/reset-password',
      });
      if (supaError) throw supaError;
      setResetSent(true);
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Failed to send reset email.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleOAuth = async (provider: 'google' | 'apple') => {
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.origin + '/' },
    });
    if (oauthError) setError(oauthError.message);
  };

  // ---------------------------------------------------------------------------
  // Style helpers
  // ---------------------------------------------------------------------------
  const fieldInputStyle: React.CSSProperties = {
    ...baseInputStyle,
    width: '100%',
    padding: '13px 16px',
    fontSize: '15px',
    borderRadius: C.radius.md,
    background: C.bg.input,
    border: `1px solid ${C.border}`,
    color: C.text.primary,
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
  };

  const fieldInputWithIconStyle: React.CSSProperties = {
    ...fieldInputStyle,
    paddingRight: '48px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '12px',
    fontWeight: 700,
    color: C.text.secondary,
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  // An already-signed-in visitor has no business being shown a sign-in form.
  //
  // The predicate is ProtectedRoute's, inverted, and deliberately not a lighter
  // one: `!authLoading && !passwordRecovery && user && tenant`. Anything weaker
  // — a token sitting in localStorage, or `user` alone — would send an EXPIRED
  // or otherwise invalid session to the dashboard, where ProtectedRoute applies
  // this same rule and bounces it straight back here. Sharing one rule is what
  // stops the two pages disagreeing and ping-ponging the user between them.
  //
  // `user` is populated only after the Supabase client has validated or
  // refreshed the session, and `tenant` only after the org claims resolve, so an
  // expired token leaves both null and the form is shown — which is correct.
  // passwordRecovery is excluded because a recovering user must reach
  // /reset-password, not the dashboard. C27 (c).
  if (!authLoading && !passwordRecovery && user && tenant) {
    return <Navigate to="/" replace />;
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        background: C.bg.deep,
        overflow: 'hidden',
      }}
    >
      {/* ============================================================ */}
      {/* LEFT PANEL — 60%: Cinematic background + branding            */}
      {/* ============================================================ */}
      {!isNarrow && (
        <div
          style={{
            flex: '0 0 60%',
            position: 'relative',
            overflow: 'hidden',
            background: `linear-gradient(145deg, ${C.bg.deep} 0%, #140709 35%, #1c0a0e 65%, #16080b 100%)`,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            padding: '64px 72px',
          }}
        >
          {/* Decorative orbs */}
          <div style={orbStyle(520, C.accent.indigo, '-120px', '-160px', 0)} />
          <div style={orbStyle(380, C.accent.violet, '40%', '-80px', 600)} />
          <div style={orbStyle(280, C.accent.emerald, '70%', '15%', 1200)} />
          <div style={orbStyle(200, C.accent.indigo, '20%', '40%', 300)} />

          {/* Subtle grid texture overlay */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `
              linear-gradient(rgba(230,57,70,0.04) 1px, transparent 1px),
              linear-gradient(90deg, rgba(230,57,70,0.04) 1px, transparent 1px)
            `,
              backgroundSize: '60px 60px',
              pointerEvents: 'none',
            }}
          />

          {/* Vignette gradient overlay */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to right, transparent 60%, rgba(5,5,10,0.4) 100%)',
              pointerEvents: 'none',
            }}
          />

          {/* Content — sits above overlays */}
          <div style={{ position: 'relative', zIndex: 1, maxWidth: '540px' }}>
            {/* Glyph + wordmark */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                marginBottom: '72px',
                animation: 'kReveal 0.6s cubic-bezier(0.16, 1, 0.3, 1) both',
              }}
            >
              <KanteraGlyph />
              <div style={{ lineHeight: 1.1 }}>
                <span
                  style={{
                    display: 'block',
                    fontSize: '20px',
                    fontWeight: 800,
                    color: C.text.bright,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    fontFamily: 'var(--k-font-display)',
                  }}
                >
                  Kantera
                </span>
                <span
                  style={{
                    display: 'block',
                    fontSize: '9px',
                    fontWeight: 600,
                    color: 'var(--k-brand)',
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    fontFamily: 'var(--k-font-mono)',
                    marginTop: '3px',
                  }}
                >
                  Soccer Intelligence
                </span>
              </div>
            </div>

            {/* Testimonial — quote mark + large editorial quote + attribution */}
            <div style={{ animation: 'kReveal 0.7s cubic-bezier(0.16, 1, 0.3, 1) 120ms both' }}>
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="var(--k-brand)"
                aria-hidden="true"
                style={{ marginBottom: '24px', opacity: 0.9 }}
              >
                <path d="M9.5 6C6.5 7.5 5 10 5 13.5V18h5v-5H7.5c0-2 .8-3.3 2.8-4.2L9.5 6zm9 0c-3 1.5-4.5 4-4.5 7.5V18h5v-5h-2.5c0-2 .8-3.3 2.8-4.2L18.5 6z" />
              </svg>
              <blockquote
                style={{
                  fontSize: 'clamp(28px, 3.2vw, 40px)',
                  fontWeight: 600,
                  color: C.text.bright,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.15,
                  margin: '0 0 32px',
                  fontFamily: 'var(--k-font-display)',
                }}
              >
                Watching my kid score on the{' '}
                <span style={{ color: 'var(--k-brand-hover)' }}>AI-clipped goal</span> — five
                seconds after the final whistle — that was the moment.
              </blockquote>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: 'var(--k-brand)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#fff',
                    fontFamily: 'var(--k-font-display)',
                  }}
                >
                  JK
                </div>
                <div style={{ lineHeight: 1.3 }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: C.text.primary }}>
                    Jordan Kim
                  </div>
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: C.text.muted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      fontFamily: 'var(--k-font-mono)',
                      marginTop: '2px',
                    }}
                  >
                    Parent · U13 Wolves
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom legal text */}
          <div
            style={{
              position: 'absolute',
              bottom: '32px',
              left: '72px',
              fontSize: '11px',
              color: C.text.muted,
              fontFamily: 'var(--k-font-mono)',
              letterSpacing: '0.08em',
            }}
          >
            © {new Date().getFullYear()} Kantera · Built for the beautiful game.
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* RIGHT PANEL — 40%: Glass auth card                           */}
      {/* ============================================================ */}
      <div
        style={{
          flex: isNarrow ? 1 : '0 0 40%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 32px',
          position: 'relative',
          background: `linear-gradient(180deg, ${C.bg.base} 0%, ${C.bg.deep} 100%)`,
          borderLeft: `1px solid ${C.border}`,
        }}
      >
        {/* Subtle top glow */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '300px',
            height: '1px',
            background: `linear-gradient(90deg, transparent, ${C.accent.indigo}, transparent)`,
            opacity: 0.4,
          }}
        />

        <div
          style={{
            width: '100%',
            maxWidth: '380px',
            animation: 'kReveal 0.65s cubic-bezier(0.16, 1, 0.3, 1) 100ms both',
          }}
        >
          {/* -------------------------------------------------------- */}
          {/* RESET PASSWORD VIEW                                      */}
          {/* -------------------------------------------------------- */}
          {showReset ? (
            <>
              <div style={{ marginBottom: '32px' }}>
                <h2
                  style={{
                    fontSize: '24px',
                    fontWeight: 800,
                    color: C.text.primary,
                    letterSpacing: '-0.03em',
                    margin: '0 0 8px',
                  }}
                >
                  Reset password
                </h2>
                <p style={{ fontSize: '14px', color: C.text.muted, margin: 0, lineHeight: 1.6 }}>
                  {resetSent
                    ? 'Check your inbox for a reset link.'
                    : "Enter your email and we'll send you a link."}
                </p>
              </div>

              {resetSent ? (
                <div
                  style={{
                    padding: '20px',
                    background: 'rgba(52,211,153,0.08)',
                    border: `1px solid rgba(52,211,153,0.2)`,
                    borderRadius: C.radius.md,
                    textAlign: 'center',
                  }}
                >
                  <p
                    style={{
                      color: C.accent.emerald,
                      fontSize: '14px',
                      margin: '0 0 16px',
                      lineHeight: 1.6,
                    }}
                  >
                    Reset link sent to <strong>{resetEmail}</strong>
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setShowReset(false);
                      setResetSent(false);
                      setResetEmail('');
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: C.text.muted,
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontFamily: 'inherit',
                      transition: 'color 150ms ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = C.accent.indigo;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = C.text.muted;
                    }}
                  >
                    Back to sign in
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleReset}
                  style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
                >
                  {resetError && (
                    <div
                      role="alert"
                      style={{
                        padding: '12px 16px',
                        background: `rgba(239,68,68,0.08)`,
                        border: `1px solid rgba(239,68,68,0.2)`,
                        borderRadius: C.radius.sm,
                        color: '#f87171',
                        fontSize: '13px',
                        lineHeight: 1.5,
                      }}
                    >
                      {resetError}
                    </div>
                  )}

                  <div>
                    <label htmlFor="reset-email" style={labelStyle}>
                      Email address
                    </label>
                    <input
                      id="reset-email"
                      type="email"
                      placeholder="your@email.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      disabled={resetLoading}
                      style={fieldInputStyle}
                      onFocus={applyInputFocus}
                      onBlur={removeInputFocus}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={resetLoading}
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: `linear-gradient(135deg, ${C.accent.indigo} 0%, ${C.accent.violet} 100%)`,
                      color: '#fff',
                      border: 'none',
                      borderRadius: C.radius.md,
                      fontSize: '15px',
                      fontWeight: 700,
                      fontFamily: 'inherit',
                      cursor: resetLoading ? 'wait' : 'pointer',
                      opacity: resetLoading ? 0.7 : 1,
                      transition: 'all 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                      letterSpacing: '-0.01em',
                    }}
                    onMouseEnter={(e) => {
                      if (!resetLoading) {
                        e.currentTarget.style.boxShadow =
                          '0 0 24px rgba(230,57,70,0.35), 0 4px 16px rgba(0,0,0,0.4)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {resetLoading ? 'Sending…' : 'Send Reset Link'}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setShowReset(false);
                      setResetError(null);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: C.text.muted,
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontFamily: 'inherit',
                      textAlign: 'center',
                      padding: '4px',
                      transition: 'color 150ms ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = C.accent.indigo;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = C.text.muted;
                    }}
                  >
                    Back to sign in
                  </button>
                </form>
              )}
            </>
          ) : (
            <>
              {/* ------------------------------------------------------ */}
              {/* MAIN LOGIN VIEW                                         */}
              {/* ------------------------------------------------------ */}

              {/* Header */}
              <div style={{ marginBottom: '32px' }}>
                <h2
                  style={{
                    fontSize: '28px',
                    fontWeight: 700,
                    color: C.text.primary,
                    letterSpacing: '-0.035em',
                    margin: '0 0 8px',
                    fontFamily: 'var(--k-font-display)',
                  }}
                >
                  Welcome back.
                </h2>
                <p style={{ fontSize: '14px', color: C.text.muted, margin: 0, lineHeight: 1.6 }}>
                  Sign in to your club.
                </p>
              </div>

              {/* C23 — signed in, but no club resolved.
                  buildTenant now returns null when app_metadata.org_id is
                  absent, so such a session lands here rather than on a dashboard
                  that would 401 on every request. There is deliberately NO
                  self-service repair offered: the only writers of
                  app_metadata.org_id are registration (needs a new account) and
                  invitation acceptance (needs an org claim already), so telling
                  this user to "complete their profile" would be a loop. The
                  honest instruction is to ask for a fresh invitation. */}
              {user && !tenant && (
                <div
                  role="alert"
                  style={{
                    padding: '12px 16px',
                    background: `rgba(245,158,11,0.08)`,
                    border: `1px solid rgba(245,158,11,0.25)`,
                    borderRadius: C.radius.sm,
                    color: '#fbbf24',
                    fontSize: '13px',
                    lineHeight: 1.5,
                    marginBottom: '20px',
                  }}
                >
                  This account isn&rsquo;t linked to a club yet. Ask your club admin to send you a
                  new invitation.
                </div>
              )}

              {/* A sign-in that failed on the RETURN leg — provider not enabled,
                  redirect URL not allow-listed, consent declined. Before this,
                  every one of those came back to this page showing nothing at
                  all, which is indistinguishable from never having clicked the
                  button. That is why "Google Auth is broken" was the most any
                  report could say. `error_description` is the provider's own
                  wording, so it is shown verbatim rather than reworded. */}
              {authError && (
                <div
                  role="alert"
                  style={{
                    padding: '12px 16px',
                    background: `rgba(239,68,68,0.08)`,
                    border: `1px solid rgba(239,68,68,0.25)`,
                    borderRadius: C.radius.sm,
                    color: '#fca5a5',
                    fontSize: '13px',
                    lineHeight: 1.5,
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                  }}
                >
                  <span style={{ flex: 1 }}>{authError}</span>
                  <button
                    type="button"
                    onClick={clearAuthError}
                    aria-label={t('common.close')}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'inherit',
                      cursor: 'pointer',
                      fontSize: '16px',
                      lineHeight: 1,
                      padding: 0,
                    }}
                  >
                    &times;
                  </button>
                </div>
              )}

              {/* Error banner */}
              {error && (
                <div
                  role="alert"
                  style={{
                    padding: '12px 16px',
                    background: `rgba(239,68,68,0.08)`,
                    border: `1px solid rgba(239,68,68,0.2)`,
                    borderRadius: C.radius.sm,
                    color: '#f87171',
                    fontSize: '13px',
                    lineHeight: 1.5,
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ flexShrink: 0, marginTop: '1px' }}
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {error}
                </div>
              )}

              {/* SSO buttons — above the email form (mockup order) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  { provider: 'google' as const, label: 'Continue with Google', Icon: GoogleIcon },
                  { provider: 'apple' as const, label: 'Continue with Apple', Icon: AppleIcon },
                ].map(({ provider, label, Icon }) => (
                  <button
                    key={provider}
                    type="button"
                    onClick={() => handleOAuth(provider)}
                    onMouseEnter={(e) => {
                      setBorderColor(e.currentTarget, C.borderHover);
                      e.currentTarget.style.background = C.bg.hover;
                    }}
                    onMouseLeave={(e) => {
                      setBorderColor(e.currentTarget, C.border);
                      e.currentTarget.style.background = C.bg.card;
                    }}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      padding: '12px 16px',
                      background: C.bg.card,
                      border: `1px solid ${C.border}`,
                      borderRadius: C.radius.md,
                      color: C.text.primary,
                      fontSize: '14px',
                      fontWeight: 600,
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                      transition: 'all 150ms ease',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    <Icon />
                    {label}
                  </button>
                ))}
              </div>

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0' }}>
                <div style={{ flex: 1, height: '1px', background: C.border }} />
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: C.text.muted,
                    whiteSpace: 'nowrap',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    fontFamily: 'var(--k-font-mono)',
                  }}
                >
                  or continue with email
                </span>
                <div style={{ flex: 1, height: '1px', background: C.border }} />
              </div>

              {/* Form */}
              <form
                onSubmit={handleLogin}
                style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
              >
                {/* Email */}
                <div>
                  <label htmlFor="login-email" style={labelStyle}>
                    Email address
                  </label>
                  <input
                    id="login-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    style={fieldInputStyle}
                    autoComplete="email"
                    onFocus={applyInputFocus}
                    onBlur={removeInputFocus}
                  />
                </div>

                {/* Password */}
                <div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      marginBottom: '8px',
                    }}
                  >
                    <label htmlFor="login-password" style={{ ...labelStyle, marginBottom: 0 }}>
                      Password
                    </label>
                    {/* KI-031 §1.6 — link to the dedicated /forgot-password route.
                        The legacy in-modal reset path (showReset state) is kept as a
                        backup but no longer exposed from the primary CTA. */}
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: C.accent.indigo,
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 600,
                        fontFamily: 'inherit',
                        padding: 0,
                        transition: 'opacity 150ms ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '0.75';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      style={fieldInputWithIconStyle}
                      autoComplete="current-password"
                      onFocus={applyInputFocus}
                      onBlur={removeInputFocus}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      style={{
                        position: 'absolute',
                        right: '14px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: C.text.muted,
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'color 150ms ease',
                      }}
                    >
                      {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                </div>

                {/* Sign In CTA */}
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'var(--k-brand)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: C.radius.md,
                    fontSize: '15px',
                    fontWeight: 700,
                    fontFamily: 'inherit',
                    cursor: loading ? 'wait' : 'pointer',
                    opacity: loading ? 0.7 : 1,
                    transition: 'all 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                    letterSpacing: '-0.01em',
                    boxShadow: '0 4px 14px rgba(230,57,70,0.3)',
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.currentTarget.style.boxShadow =
                        '0 0 24px rgba(230,57,70,0.35), 0 4px 16px rgba(0,0,0,0.4)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(230,57,70,0.2)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {loading ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        style={{ animation: 'kFloat 1s ease-in-out infinite' }}
                      >
                        <path d="M21 12a9 9 0 11-6.219-8.56" />
                      </svg>
                      Signing in…
                    </span>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>

              {/* New here — start a club */}
              <p
                style={{
                  marginTop: '28px',
                  textAlign: 'center',
                  fontSize: '13px',
                  color: C.text.muted,
                  lineHeight: 1.5,
                }}
              >
                New here?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/register')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--k-brand-hover)',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 700,
                    fontFamily: 'inherit',
                    padding: 0,
                    textDecoration: 'none',
                  }}
                >
                  Start a club, free.
                </button>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
